import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/SimpleAuthContext';
import { useToast } from '../../contexts/ToastContext';
import { opportunityService } from '../../services/opportunityService';
import './ApplicationManagement.css';

const ApplicationManagement = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [applications, setApplications] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOpportunity, setSelectedOpportunity] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    opportunity: '',
    search: ''
  });

  useEffect(() => {
    if (!user || (!user.is_staff && !['admin', 'manager'].includes(user.role))) {
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [appsData, oppsData] = await Promise.all([
        opportunityService.getAllApplications(),
        opportunityService.getOpportunities({ page_size: 100 })
      ]);
      
      setApplications(appsData.results || []);
      setOpportunities(oppsData.results || []);
    } catch (error) {
      console.error('Error loading application data:', error);
      showError('Failed to load application data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (applicationId, newStatus) => {
    try {
      await opportunityService.updateApplicationStatus(applicationId, newStatus);
      showSuccess(`Application status updated to ${newStatus}`);
      loadData(); // Refresh data
    } catch (error) {
      console.error('Error updating application status:', error);
      showError('Failed to update application status');
    }
  };

  const exportApplications = () => {
    const filteredApps = getFilteredApplications();
    const csvContent = [
      ['Opportunity', 'Applicant Name', 'Email', 'Phone', 'Status', 'Applied Date', 'Reviewed Date'].join(','),
      ...filteredApps.map(app => [
        app.opportunity?.title || '',
        `${app.user?.first_name || ''} ${app.user?.last_name || ''}`.trim(),
        app.user?.email || '',
        app.user?.phone || '',
        app.status,
        new Date(app.created_at).toLocaleDateString(),
        app.reviewed_at ? new Date(app.reviewed_at).toLocaleDateString() : ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `opportunity_applications_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getFilteredApplications = () => {
    return applications.filter(app => {
      const matchesStatus = !filters.status || app.status === filters.status;
      const matchesOpportunity = !filters.opportunity || app.opportunity?.id.toString() === filters.opportunity;
      const matchesSearch = !filters.search || 
        app.user?.first_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        app.user?.last_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        app.user?.email?.toLowerCase().includes(filters.search.toLowerCase()) ||
        app.opportunity?.title?.toLowerCase().includes(filters.search.toLowerCase());
      
      return matchesStatus && matchesOpportunity && matchesSearch;
    });
  };

  if (!user || (!user.is_staff && !['admin', 'manager'].includes(user.role))) {
    return (
      <div className="application-management">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to view application management.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="application-management">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading applications...</p>
        </div>
      </div>
    );
  }

  const filteredApplications = getFilteredApplications();

  return (
    <div className="application-management">
      <div className="header">
        <h1>📋 Application Management</h1>
        <p>Manage and review opportunity applications</p>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search by name, email, or opportunity..."
            value={filters.search}
            onChange={(e) => setFilters({...filters, search: e.target.value})}
          />
        </div>
        
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
          >
            <option value="">All Statuses</option>
            <option value="applied">Applied</option>
            <option value="under_review">Under Review</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="waitlisted">Waitlisted</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Opportunity:</label>
          <select
            value={filters.opportunity}
            onChange={(e) => setFilters({...filters, opportunity: e.target.value})}
          >
            <option value="">All Opportunities</option>
            {opportunities.map(opp => (
              <option key={opp.id} value={opp.id}>{opp.title}</option>
            ))}
          </select>
        </div>

        <button onClick={exportApplications} className="btn btn-outline">
          📊 Export CSV
        </button>
      </div>

      {/* Statistics */}
      <div className="stats">
        <div className="stat-card">
          <h3>{filteredApplications.length}</h3>
          <p>Total Applications</p>
        </div>
        <div className="stat-card">
          <h3>{filteredApplications.filter(app => app.status === 'applied').length}</h3>
          <p>Pending Review</p>
        </div>
        <div className="stat-card">
          <h3>{filteredApplications.filter(app => app.status === 'accepted').length}</h3>
          <p>Accepted</p>
        </div>
        <div className="stat-card">
          <h3>{filteredApplications.filter(app => app.status === 'rejected').length}</h3>
          <p>Rejected</p>
        </div>
      </div>

      {/* Applications Table */}
      <div className="applications-table">
        {filteredApplications.length === 0 ? (
          <div className="no-applications">
            <p>No applications found matching your criteria.</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Applicant</th>
                <th>Opportunity</th>
                <th>Applied Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredApplications.map(application => (
                <tr key={application.id}>
                  <td>
                    <div className="applicant-info">
                      <strong>{application.user?.first_name} {application.user?.last_name}</strong>
                      <div className="contact-info">
                        <small>{application.user?.email}</small>
                        {application.user?.phone && <small>{application.user?.phone}</small>}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="opportunity-info">
                      <strong>{application.opportunity?.title}</strong>
                      <small>{application.opportunity?.opportunity_type}</small>
                    </div>
                  </td>
                  <td>{new Date(application.created_at).toLocaleDateString()}</td>
                  <td>
                    <span className={`status-badge status-${application.status}`}>
                      {application.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <div className="actions">
                      <select
                        value={application.status}
                        onChange={(e) => handleStatusUpdate(application.id, e.target.value)}
                        className="status-select"
                      >
                        <option value="applied">Applied</option>
                        <option value="under_review">Under Review</option>
                        <option value="accepted">Accepted</option>
                        <option value="rejected">Rejected</option>
                        <option value="waitlisted">Waitlisted</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ApplicationManagement;
